import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Check, X, Image } from 'lucide-react';
import { SpecialDate } from '@/types/specialDate';
import { specialDatesApi } from '@/services/specialDatesApi';
import toast from 'react-hot-toast';

// Firestore Timestamp objesini string'e çevir
const formatFirestoreValue = (value: any): string => {
  if (!value) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && ('_seconds' in value || 'seconds' in value)) {
    const seconds = value._seconds ?? value.seconds;
    const d = new Date(seconds * 1000);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}-${day}`;
  }
  if (value instanceof Date) {
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${month}-${day}`;
  }
  return String(value);
};

export const SpecialDatesList: React.FC = () => {
  const navigate = useNavigate();
  const [specialDates, setSpecialDates] = useState<SpecialDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSpecialDates();
  }, []);

  const loadSpecialDates = async () => {
    try {
      setLoading(true);
      const data = await specialDatesApi.getAllSpecialDates();
      setSpecialDates(data);
    } catch (error: any) {
      console.error('Özel tarihler yüklenirken hata:', error);
      toast.error(error.message || 'Özel tarihler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`"${name}" özel tarihini silmek istediğinize emin misiniz?`)) {
      try {
        await specialDatesApi.deleteSpecialDate(id);
        toast.success('✅ Özel tarih silindi!');
        loadSpecialDates();
      } catch (error: any) {
        console.error('Özel tarih silinirken hata:', error);
        toast.error(error.message || 'Özel tarih silinemedi');
      }
    }
  };

  const formatDate = (date: Date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR');
  };

  const handleToggleActive = async (specialDate: SpecialDate) => {
    try {
      await specialDatesApi.updateSpecialDate(specialDate.id, {
        isActive: !specialDate.isActive,
      });
      toast.success(specialDate.isActive ? '✅ Özel tarih deaktif edildi' : '✅ Özel tarih aktif edildi');
      loadSpecialDates();
    } catch (error: any) {
      toast.error(error.message || 'Durum güncellenemedi');
    }
  };

  const filteredSpecialDates = specialDates.filter((specialDate) => {
    return (
      specialDate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      specialDate.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">📅 Özel Tarihler</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Toplam {filteredSpecialDates.length} özel tarih
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/special-dates/new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          <Plus size={20} />
          Yeni Özel Tarih
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Tarih adı veya açıklama ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          {filteredSpecialDates.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">Özel tarih bulunamadı</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Adı</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Tarih</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Tema</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Banner</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Durum</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSpecialDates.map((specialDate) => (
                    <tr key={specialDate.id} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {specialDate.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatFirestoreValue(specialDate.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {specialDate.emoji} {specialDate.emoji ? '' : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {specialDate.daysBeforeDisplay} gün
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleToggleActive(specialDate)}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition ${
                            specialDate.isActive
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}
                        >
                          {specialDate.isActive ? (
                            <>
                              <Check size={14} /> Aktif
                            </>
                          ) : (
                            <>
                              <X size={14} /> Deaktif
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <button
                          onClick={() => navigate(`/admin/special-dates/${specialDate.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(specialDate.id, specialDate.name)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpecialDatesList;
